import { DEFAULT_LOCALE } from "@/lib/i18n";

/*
 * PRE-PAINT PREFERENCE BOOTSTRAP
 *
 * Deliberately NOT a `"use client"` module. The script below has to be rendered
 * by the root layout, which is a Server Component, and a Server Component that
 * imports from a client module gets client *references* rather than the string
 * values. Keeping the contract in a plain module lets both sides import it: the
 * providers read the storage keys, the layout renders the script.
 */

/** What the user picked, including the `system` pass-through. */
export type Theme = "light" | "dark" | "system";
/** What is actually on screen right now. */
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "blink.business-os.theme";
export const LOCALE_STORAGE_KEY = "blink.business-os.locale";

export const DEFAULT_THEME: Theme = "system";
export const DARK_QUERY = "(prefers-color-scheme: dark)";

/**
 * Reads both persisted preferences and writes them onto `<html>` while the
 * document is still being parsed.
 *
 * Must stay in sync with `applyTheme` in the theme provider and `applyLocale` in
 * the locale provider — this is the same assignment, made earlier.
 *
 * `classList.toggle` and `dataset` are used rather than assigning `className`,
 * because `<html>` also carries the font-variable classes from `next/font`.
 */
const BOOTSTRAP = `try{
var e=document.documentElement;
var t=localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)});
if(t!=="light"&&t!=="dark")t=${JSON.stringify(DEFAULT_THEME)};
var d=t==="dark"||(t==="system"&&matchMedia(${JSON.stringify(DARK_QUERY)}).matches);
e.classList.toggle("dark",d);
e.dataset.theme=d?"dark":"light";
e.style.colorScheme=d?"dark":"light";
var l=localStorage.getItem(${JSON.stringify(LOCALE_STORAGE_KEY)});
if(l!=="ar"&&l!=="en")l=${JSON.stringify(DEFAULT_LOCALE)};
e.lang=l;
e.dir=l==="ar"?"rtl":"ltr";
}catch(e){}`
  .replace(/\n/g, "");

/**
 * Belongs in `<head>`, ahead of any painted content.
 *
 * The server has no request-scoped theme or locale, so it renders the product
 * defaults — light-resolving `system`, and Arabic/RTL. Anything that corrects
 * those has to run before the first paint or the user watches it happen: a
 * dark-theme user gets a full-brightness flash, and an English user gets an
 * RTL frame that flips. An effect cannot do this (it runs after paint), and
 * neither can a script placed inside `<body>` — by then the browser has the
 * body element and the stylesheet, which is everything it needs to paint the
 * wrong frame.
 */
export function PreferenceBootstrap() {
  return <script dangerouslySetInnerHTML={{ __html: BOOTSTRAP }} suppressHydrationWarning />;
}
